import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home, Cpu, BarChart2, Database, Shield, FileText, User, Merge, Server,
  Brain, FolderOpenDot, Layers3, Settings, Clock, BrainCog,
  PanelLeftOpen, PanelRightOpen,
  AppWindow,
  MessageSquare,
  UserRound,
  MessageCircle,
  LayoutDashboard,
  ShieldCheck,
  Activity
} from 'lucide-react';
import { useTheme } from './Theme';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RoutePrivilege {
  id: string;
  hasAccess: boolean;
  children?: RoutePrivilege[];
}

interface NavItem {
  to: string;
  icon: any;
  label: string;
  id: string;
  children?: NavItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const iconSize = collapsed ? 20 : 16;
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const routeAccess: RoutePrivilege[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_routes') || '[]');
    } catch {
      return [];
    }
  }, []);

  const hasAccess = (id: string, routes: RoutePrivilege[] = routeAccess): boolean => {
    for (const route of routes) {
      if (route.id === id) return route.hasAccess ?? false;
      if (Array.isArray(route.children) && route.children.length > 0) {
        const found = hasAccess(id, route.children);
        if (found) return true;
      }
    }
    return false;
  };

  const renderNavLink = (item: NavItem) => {
    if (!hasAccess(item.id)) return null;

    return (
      <div key={item.id}>
        <NavLink
          to={item.to}
          className={({ isActive }) =>
            `flex items-center ${collapsed ? 'justify-center' : 'gap-2'} px-3 py-2.5 rounded-lg transition-all duration-150 text-[13px] font-medium border-l-2 ${collapsed ? 'border-l-transparent' : ''} ${isActive
              ? (isLight ? 'bg-indigo-50 text-indigo-700 border-l-indigo-500' : 'bg-neutral-800 text-white border-l-indigo-500')
              : (isLight ? 'border-l-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900' : 'border-l-transparent text-gray-300 hover:bg-neutral-800/70 hover:text-white')
            }`
          }
          title={collapsed ? item.label : undefined}
        >
          <item.icon size={iconSize} className="shrink-0" />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </NavLink>

        {/* Render nested children */}
        {item.children && item.children.length > 0 && (
          <div className={`ml-${collapsed ? 0 : 4} pl-3 border-l ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            {item.children.map((child) => renderNavLink(child))}
          </div>
        )}
      </div>
    );
  };

  const navItems: { section: string; links: NavItem[] }[] = [

    {
      section: 'Platform',
      links: [
        { to: '/portal/workflows', icon: Cpu, label: 'Orchestrations', id: 'b2c3d4e5-f6g7-8901-bcde-f12345678901' },
        { to: '/portal/applications', icon: AppWindow, label: 'Applications', id: '6f3a0f4c-20b7-4c2a-b4c1-7caaed2358b7' },
        { to: '/portal/chatagent', icon: MessageCircle, label: 'AI Chat Bots', id: 'd4e5f6g7-h8i9-0123-def1-234567890123' },
        { to: '/portal/agents', icon: Brain, label: 'Functions', id: 'd4e5f6g7-h8i9-0123-def1-234567890123' },
        { to: '/portal/knowledge-base', icon: Layers3, label: 'Knowledge Workspace', id: 'h8i9j0k1-l2m3-4567-2345-678901234567' },
      ],
    },
    {
      section: 'Configurations',
      links: [
        { to: '/portal/configurations/data-ingestion', icon: FolderOpenDot, label: 'Data Models', id: 'f6g7h8i9-j0k1-2345-f123-456789012345' },
        { to: '/portal/configurations/llm-routing', icon: Merge, label: 'LLM Routing', id: 'k1l2m3n4-o5p6-7890-5678-901234567890' },
        { to: '/portal/configurations/data-vault', icon: Database, label: 'Data Vault', id: 'o5p6q7r8-s9t0-1234-9012-345678901234' },
        { to: '/portal/configurations/schedules', icon: Clock, label: 'Schedules', id: 's9t0u1v2-w3x4-5678-3456-789012345678' },
        { to: '/portal/intent-orchestration', icon: BrainCog, label: 'Intent Orchestration', id: 'w3x4y5z6-a7b8-9012-7890-123456789012' },
        { to: '/portal/deployments', icon: Server, label: 'Deployments', id: 'a7b8c9d0-e1f2-3456-1234-567890123456' },
      ],
    },
    {
      section: 'MCP Access Manager',
      links: [
        { to: '/portal/mcpdashboard', icon: LayoutDashboard, label: 'MCP Dashboard', id: 'a1d2f3b4-5c6e-47a8-9b01-2c3d4e5f6a7b' },
        { to: '/portal/mcpdevelopers', icon: ShieldCheck, label: 'Developer Access', id: 'b2e3c4d5-6f7a-48b9-0c12-3d4e5f6a7b8c' },
        { to: '/portal/mcpresources', icon: Database, label: 'MCP Resources', id: 'c3f4d5e6-7a8b-49c0-1d23-4e5f6a7b8c9d' },
        { to: '/portal/mcplivemonitor', icon: Activity, label: 'Monitoring', id: 'd4a5e6f7-8b9c-40d1-2e34-5f6a7b8c9d0e' },
      ],
    },
    {
      section: 'Access & System',
      links: [
        { to: '/portal/connections', icon: Cpu, label: 'Connections & Keys', id: 'e1f2g3h4-i5j6-7890-5678-901234567890' },
        { to: '/portal/access-settings', icon: Shield, label: 'Access Settings', id: 'h4i5j6k7-l8m9-0123-8901-234567890123' },
        { to: '/portal/system-settings', icon: Settings, label: 'System Settings', id: 'm9n0o1p2-q3r4-5678-3456-789012345678' },
        { to: '/portal/my-account', icon: User, label: 'My Account', id: 'n0o1p2q3-r4s5-6789-4567-890123456789' },
      ],
    },
    {
      section: 'Monitoring',
      links: [
        { to: '/portal/reports', icon: BarChart2, label: 'Reports', id: 'p2q3r4s5-t6u7-8901-6789-012345678901' },
        { to: '/portal/logs', icon: FileText, label: 'Logs', id: 'o1p2q3r4-s5t6-7890-5678-901234567890' },
      ],
    },
  ];

  return (
    <aside
      className={`${collapsed ? 'w-20' : 'w-56'
        } ${isLight ? 'bg-white border-r border-neutral-200' : 'bg-neutral-950 border-r border-neutral-800'} pt-4 fixed top-16 bottom-0 left-0 flex flex-col transition-all duration-200`}
    >
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-3 pb-4">
          {hasAccess('a1b2c3d4-e5f6-7890-abcd-ef1234567890') && (
            <NavLink to="/portal/home" className={({ isActive }) =>
              `flex items-center ${collapsed ? 'justify-center' : 'gap-2'} px-3 py-2.5 rounded-lg transition-all duration-150 text-[13px] font-medium border-l-2 ${collapsed ? 'border-l-transparent' : ''} ${isActive
                ? (isLight ? 'bg-indigo-50 text-indigo-700 border-l-indigo-500' : 'bg-neutral-800 text-white border-l-indigo-500')
                : (isLight ? 'border-l-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900' : 'border-l-transparent text-gray-300 hover:bg-neutral-800/70 hover:text-white')
              }`}>
              <Home size={iconSize} className="shrink-0" />
              {!collapsed && <span className="truncate">Home</span>}
            </NavLink>
          )}
        </div>

        <nav className="space-y-1 px-2">
          {navItems.map((section) => {
            const visibleLinks = section.links.filter((item) => hasAccess(item.id));
            if (visibleLinks.length === 0) return null;

            return (
              <div key={section.section}>
                {!collapsed && (
                  <div className={`text-[11px] font-semibold uppercase tracking-wider mt-5 mb-1.5 px-2 first:mt-2 ${isLight ? 'text-neutral-400' : 'text-gray-500'}`}>
                    {section.section}
                  </div>
                )}
                {visibleLinks.map(renderNavLink)}
              </div>
            );
          })}
        </nav>
      </div>

      <div className={`border-t ${isLight ? 'border-neutral-200' : 'border-neutral-800'} p-2 flex justify-center`}>
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          className={`p-2 rounded-lg transition-colors focus:outline-none ${isLight ? 'text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100' : 'text-gray-400 hover:text-white hover:bg-neutral-800/70'}`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelRightOpen size={20} />}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;



// import React, { useState } from 'react';
// import { NavLink } from 'react-router-dom';
// import {
//   Home,
//   Cpu,
//   BarChart2,
//   Database,
//   Shield,
//   FileText,
//   User,
//   Merge,
//   Server,
//   Brain,
//   FolderOpenDot,
//   Layers3,
//   Settings,
//   Clock,
//   BrainCog,
//   ChevronLeft,
//   ChevronRight,
//   PanelLeftOpen,
//   PanelRightOpen,
// } from 'lucide-react';

// interface SidebarProps {
//   collapsed: boolean;
//   setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
// }

// const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {

//   const linkClass = ({ isActive }: any) =>
//   `flex items-center ${
//     collapsed ? 'justify-center' : 'gap-2'
//   } px-3 py-2 rounded-md transition-all text-[13px] font-medium ${
//     isActive
//       ? 'bg-neutral-800 text-white'
//       : 'text-gray-200 hover:bg-neutral-800 hover:text-white'
//   }`;

//   const iconSize = collapsed ? 20 : 16;
//   const navItems = [
//     {
//       section: 'Platform',
//       links: [
//         { to: '/portal/workflows', icon: Cpu, label: 'Orchestrations' ,id:'b2c3d4e5-f6g7-8901-bcde-f12345678901'},
//         { to: '/portal/agents', icon: Brain, label: 'Functions' ,id:'d4e5f6g7-h8i9-0123-def1-234567890123'},
//       ],
//     },
//     {
//       section: 'Configurations',
//       id:'',
//       links: [
//         { to: '/portal/configurations/data-ingestion', icon: FolderOpenDot, label: 'Entities',id:'f6g7h8i9-j0k1-2345-f123-456789012345' },
//         { to: '/portal/knowledge-base', icon: Layers3, label: 'Knowledge Workspace',id:'h8i9j0k1-l2m3-4567-2345-678901234567' },
//         { to: '/portal/configurations/llm-routing', icon: Merge, label: 'LLM Routing' ,id:'k1l2m3n4-o5p6-7890-5678-901234567890'},
//         { to: '/portal/configurations/data-vault', icon: Database, label: 'Data Vault',id:'o5p6q7r8-s9t0-1234-9012-345678901234' },
//         { to: '/portal/configurations/schedules', icon: Clock, label: 'Schedules',id:'s9t0u1v2-w3x4-5678-3456-789012345678' },
//         { to: '/portal/intent-orchestration', icon: BrainCog, label: 'Intent Orchestration' ,id:'w3x4y5z6-a7b8-9012-7890-123456789012'},
//         { to: '/portal/deployments', icon: Server, label: 'Deployments' ,id:'a7b8c9d0-e1f2-3456-1234-567890123456'},
//       ],
//     },
//     {
//       section: 'Access & System',
//       links: [
//         { to: '/portal/connections', icon: Cpu, label: 'Connections & Keys',id:'e1f2g3h4-i5j6-7890-5678-901234567890' },
//         { to: '/portal/access-settings', icon: Shield, label: 'Access Settings',id:'h4i5j6k7-l8m9-0123-8901-234567890123' },
//         { to: '/portal/system-settings', icon: Settings, label: 'System Settings' ,id:'m9n0o1p2-q3r4-5678-3456-789012345678'},
//         { to: '/portal/my-account', icon: User, label: 'My Account',id:'n0o1p2q3-r4s5-6789-4567-890123456789' },
//       ],
//     },
//     {
//       section: 'Monitoring',
//       links: [
//         { to: '/portal/reports', icon: BarChart2, label: 'Reports',id:'p2q3r4s5-t6u7-8901-6789-012345678901' },
//         { to: '/portal/logs', icon: FileText, label: 'Logs',id:'o1p2q3r4-s5t6-7890-5678-901234567890' },
//       ],
//     },
//   ];

//   const toggleSidebar = () => setCollapsed((prev) => !prev);

//   return (
//    <aside
//   className={`${
//     collapsed ? 'w-20' : 'w-56'
//   } bg-neutral-950 border-r border-neutral-800 pt-4 fixed top-16 bottom-0 left-0 flex flex-col transition-all duration-200`}
// >
//   {/* Scrollable nav content */}
//   <div className="flex-1 overflow-y-auto overflow-x-hidden">
//     {/* Home Link */}
//     <div className="px-3 pb-4">
//       <NavLink to="/portal/home" className={linkClass}>
//         <Home size={iconSize} />
//         {!collapsed && <span>Home</span>}
//       </NavLink>
//     </div>

//     {/* Navigation sections */}
//     <nav className="space-y-1 px-2">
//       {navItems.map((section) => (
//         <div key={section.section}>
//           {!collapsed && (
//             <div className="text-[11px] text-gray-400 uppercase tracking-wide mt-4 mb-2 px-2">
//               {section.section}
//             </div>
//           )}
//           {section.links.map(({ to, icon: Icon, label }) => (
//             <NavLink
//               key={to}
//               to={to}
//               className={linkClass}
//               title={collapsed ? label : undefined}
//             >
//               <Icon size={iconSize} />
//               {!collapsed && <span>{label}</span>}
//             </NavLink>
//           ))}
//         </div>
//       ))}
//     </nav>
//   </div>

//   {/* Collapse/Expand Toggle - Fixed at bottom */}
//   <div className="border-t border-neutral-800 p-2 flex justify-center">
//     <button
//       onClick={toggleSidebar}
//       className="text-gray-400 hover:text-white transition"
//       title={collapsed ? 'Expand' : 'Collapse'}
//     >
//       {collapsed ? <PanelLeftOpen size={20} /> : <PanelRightOpen size={20} />}

//     </button>
//   </div>
// </aside>

//   );
// };

// export default Sidebar;
